import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";

// ─────────────────────────────────────────────────────────────────────────
// Shown when classrooms/updateTimetable responds 409 (conflict: true) —
// one or more teachers newly assigned in this grid are already teaching
// somewhere else at an overlapping day/time (see
// FacultyTimetableModule.checkFacultyConflicts on the backend). This is a
// full report, not just a count, so the admin can judge whether each
// collision is a real problem or an intentional co-teaching/temporary
// arrangement before deciding to proceed.
// ─────────────────────────────────────────────────────────────────────────

export type TimetableConflictEntry = {
    facultyUid: string;
    facultyName: string;
    day: string;
    thisClassroomPeriodLabel: string;
    thisClassroomSubject: string;
    conflictingClassroomId: string;
    conflictingClassroomName: string;
    conflictingPeriodLabel: string;
    conflictingSubject: string;
};

type Props = {
    open: boolean;
    conflicts: TimetableConflictEntry[];
    isSaving: boolean;
    onCancel: () => void;
    onProceedAnyway: () => void;
};

export function TimetableConflictDialog({ open, conflicts, isSaving, onCancel, onProceedAnyway }: Props) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
            <DialogContent className="sm:max-w-2xl border-border/60 p-0 overflow-hidden shadow-2xl">
                <DialogHeader className="p-5 border-b bg-red-50/50">
                    <DialogTitle className="text-lg font-bold flex items-center gap-2 text-red-700">
                        <AlertTriangle className="h-5 w-5" /> Scheduling conflict detected
                    </DialogTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                        {conflicts.length} teacher assignment{conflicts.length === 1 ? "" : "s"} in this timetable
                        overlap with a class they're already teaching elsewhere. Review before saving.
                    </p>
                </DialogHeader>

                <div className="max-h-[50vh] overflow-y-auto p-4 space-y-3">
                    {conflicts.map((c, i) => (
                        <div key={i} className="rounded-lg border border-red-200 bg-red-50/40 p-3">
                            <p className="text-sm font-bold text-foreground">{c.facultyName}</p>
                            <p className="text-[11px] text-muted-foreground uppercase font-black tracking-widest mt-0.5">{c.day}</p>
                            <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                                <div className="rounded-md bg-card border border-border/40 p-2">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">This timetable</p>
                                    <p className="font-semibold mt-0.5">{c.thisClassroomSubject}</p>
                                    <p className="text-muted-foreground">{c.thisClassroomPeriodLabel}</p>
                                </div>
                                <div className="rounded-md bg-card border border-border/40 p-2">
                                    <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">Already assigned</p>
                                    <p className="font-semibold mt-0.5">{c.conflictingSubject} · {c.conflictingClassroomName}</p>
                                    <p className="text-muted-foreground">{c.conflictingPeriodLabel}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <DialogFooter className="p-4 border-t bg-muted/5">
                    <Button variant="ghost" className="text-xs font-bold uppercase" onClick={onCancel} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        className="text-xs font-bold uppercase"
                        onClick={onProceedAnyway}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving…" : "Proceed Anyway"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
